import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";
import { profile } from "../../data/resume";
import { scrollToId } from "../../hooks/useLenis";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className="block"
      variants={lineVariants}
      custom={i}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden pt-28 sm:pt-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 sm:px-12 lg:px-20">
        <motion.div style={{ y: yText }} className="pt-10 lg:pt-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-rust animate-pulse" />
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-inkmuted">
              {profile.availability}
            </span>
          </motion.div>

          <h1 className="font-serif font-normal tracking-tight leading-[0.92] text-ink text-[19vw] sm:text-[15vw] lg:text-[12rem]">
            <MaskLine i={0}>Gauri</MaskLine>
            <MaskLine i={1}>
              Ambekar<span className="text-rust">.</span>
            </MaskLine>
          </h1>
        </motion.div>

        <div className="mt-10 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="lg:col-span-5"
          >
            <p className="font-serif italic text-2xl sm:text-3xl text-ink leading-tight">
              {profile.headline}
            </p>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-inkmuted">
              {profile.currentRole} · {profile.currentCompany}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="lg:col-span-5 lg:col-start-8"
          >
            <p className="font-sans text-base sm:text-lg leading-relaxed text-inksoft">
              {profile.summary}
            </p>
            <button
              data-testid="hero-scroll-cta"
              onClick={() => scrollToId("about")}
              className="group mt-8 inline-flex items-center gap-2 font-sans text-sm text-ink"
            >
              <span className="border-b border-ink pb-0.5 transition-colors duration-300 group-hover:border-rust group-hover:text-rust">
                Explore my work
              </span>
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1 group-hover:text-rust" />
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        style={{ y: yImg }}
        className="pointer-events-none absolute -right-24 top-24 h-[60vh] w-[42vw] max-w-[520px] opacity-[0.14] hidden lg:block"
      >
        <img
          src="https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
          alt=""
          className="h-full w-full object-cover grayscale"
        />
      </motion.div>
    </section>
  );
};
